import { recordMetric } from '../api';
import { hasCookieConsent } from './cookieConsent';

const VISITOR_ID_KEY = 'bypass_visitor_id';

const ANONYMOUS_TAGS = new Set([
    'page_view',
    'help_section_open',
    'protected_route_blocked',
]);

const AUTH_SESSION_TAGS = new Set([
    'auth_login_success',
    'auth_login_error',
    'auth_register_success',
    'auth_register_error',
    'auth_logout',
]);

let analyticsContext = {
    isAuthenticated: false,
    userLogin: null,
};

function createVisitorId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = Math.floor(Math.random() * 16);
        const value = char === 'x' ? random : ((random & 0x3) | 0x8);
        return value.toString(16);
    });
}

export function getVisitorId() {
    try {
        const stored = localStorage.getItem(VISITOR_ID_KEY);

        if (stored) {
            return stored;
        }

        const visitorId = createVisitorId();
        localStorage.setItem(VISITOR_ID_KEY, visitorId);
        return visitorId;
    } catch {
        return createVisitorId();
    }
}

export function setAnalyticsContext({ isAuthenticated, userLogin }) {
    analyticsContext = {
        isAuthenticated: Boolean(isAuthenticated),
        userLogin: userLogin || null,
    };
}

export function canTrack(tag) {
    if (ANONYMOUS_TAGS.has(tag)) {
        return true;
    }

    if (!analyticsContext.isAuthenticated) {
        return false;
    }

    if (AUTH_SESSION_TAGS.has(tag)) {
        return true;
    }

    return hasCookieConsent();
}

function sanitizeMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object') {
        return undefined;
    }

    const blockedKeys = new Set([
        'password',
        'old_password',
        'new_password',
        'promo_code',
        'subscription_url',
        'vpn_key',
        'token',
    ]);

    const sanitized = {};

    Object.entries(metadata).forEach(([key, value]) => {
        if (blockedKeys.has(key)) {
            return;
        }

        if (value === undefined) {
            return;
        }

        sanitized[key] = value;
    });

    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

function buildPayload(tag, metadata) {
    const payload = {
        tag,
        visitor_id: getVisitorId(),
    };

    const isAnonymousTag = ANONYMOUS_TAGS.has(tag);
    const includeUser = analyticsContext.isAuthenticated && hasCookieConsent();

    if (includeUser && analyticsContext.userLogin) {
        payload.user = analyticsContext.userLogin;
    }

    const sanitizedMetadata = sanitizeMetadata(metadata);

    if (sanitizedMetadata) {
        payload.metadata = sanitizedMetadata;
    }

    if (isAnonymousTag && payload.metadata) {
        const allowedKeys = new Set(['path', 'screen', 'referrer_host', 'from_path', 'section_id']);
        payload.metadata = Object.fromEntries(
            Object.entries(payload.metadata).filter(([key]) => allowedKeys.has(key)),
        );

        if (Object.keys(payload.metadata).length === 0) {
            delete payload.metadata;
        }
    }

    return payload;
}

export function track(tag, metadata) {
    try {
        if (!canTrack(tag)) {
            return;
        }

        const payload = buildPayload(tag, metadata);

        void recordMetric(payload).catch(() => {});
    } catch {
        // analytics must never affect UX
    }
}

export function trackErrorCode(tag, message) {
    const errorCode = typeof message === 'string'
        ? message.trim().slice(0, 120)
        : 'unknown_error';

    track(tag, { error_code: errorCode });
}

export const SCREEN_BY_PATH = {
    '/login': 'login',
    '/subscriptions': 'subscriptions',
    '/account': 'account',
    '/help': 'help',
    '/info': 'info',
    '/privacy': 'privacy',
    '/terms': 'terms',
    '/links': 'links',
    '/404': 'not_found',
};

export function getScreenForPath(pathname) {
    return SCREEN_BY_PATH[pathname] || 'other';
}
